/**
 * Tell the dashboard its cached snapshot is out of date.
 *
 * The dashboard keeps its data across navigations (stale-while-revalidate), so a
 * mutation made anywhere else in the app has to say so, or its counts and its
 * first-run hero can stay wrong for the whole TTL — create your first property,
 * go to the dashboard, and it still offers to create your first property.
 *
 * This drops FRESHNESS only, never the data: the page keeps painting what it has
 * and revalidates behind it, so invalidating never causes a blank flash.
 *
 * Lives here rather than in a composable because the callers are a mix of
 * composables and stores, and it must be importable without dragging a
 * component context along.
 */

import { devWarn } from '@shared/vue/utils/debug';

export async function invalidateDashboardSnapshot(): Promise<void> {
  try {
    const { useDashboardStore } = await import('src/stores/dashboard');
    useDashboardStore().invalidateDashboard();
  } catch (error) {
    // Non-fatal: the dashboard falls back to revalidating on its normal TTL.
    // Warned rather than swallowed silently, because a broken specifier or a
    // renamed action would otherwise turn this into a no-op nobody notices.
    devWarn('[dashboardCache] Failed to invalidate the dashboard snapshot:', error);
  }
}
